import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Archive, LogOut, Plus, Pencil, Trash2, Mail, MailOpen, Reply } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { ImageUploader } from "@/components/ImageUploader";

const EMPTY = { title: "", brand: "", category: "", era: "", ad_language: "", price_usd: 0, condition: "", dimensions: "", image: "", description: "" };
const FIELDS = [
  ["title", "title_field"], ["brand", "brand_label"], ["category", "topic"], ["era", "era"],
  ["ad_language", "ad_language"], ["condition", "condition"], ["dimensions", "dimensions"],
];

function AdForm({ initial, onSave, onCancel, onCommit, t }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handleCommit = onCommit ? (img) => onCommit({ ...form, image: img }) : undefined;
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="pv-label">{t(label)}</label>
            <Input data-testid={`adform-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
              className="rounded-none bg-[color:var(--c-surface)] border-[color:var(--c-border)] mt-1" required={k === "title" || k === "category"} />
          </div>
        ))}
        <div>
          <label className="pv-label">{t("price_field")}</label>
          <Input data-testid="adform-price" type="number" step="0.01" value={form.price_usd}
            onChange={(e) => set("price_usd", parseFloat(e.target.value) || 0)}
            className="rounded-none bg-[color:var(--c-surface)] border-[color:var(--c-border)] mt-1" />
        </div>
      </div>
      <div>
        <ImageUploader value={form.image} onChange={(v) => set("image", v)} onCommit={handleCommit} />
      </div>
      <div>
        <label className="pv-label">{t("description")}</label>
        <Textarea data-testid="adform-description" rows={4} value={form.description}
          onChange={(e) => set("description", e.target.value)}
          className="rounded-none bg-[color:var(--c-surface)] border-[color:var(--c-border)] mt-1" />
      </div>
      <div className="flex gap-3 pt-2">
        <button data-testid="adform-save" type="submit"
          className="flex-1 bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)] hover:bg-[color:var(--c-accent)] transition-colors py-3 text-xs tracking-widest uppercase font-semibold">{t("save")}</button>
        <button type="button" onClick={onCancel}
          className="flex-1 border border-[color:var(--c-ink)] py-3 text-xs tracking-widest uppercase font-semibold hover:bg-[color:var(--c-surface)] transition-colors">{t("cancel")}</button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const { t, formatPrice } = useApp();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null); // ad object or 'new'
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const loadAds = useCallback(() => api.get("/ads", { params: { sort: "newest", limit: 5000 } }).then((r) => setAds(r.data.items)), []);
  const loadMsgs = useCallback(() => api.get("/messages").then((r) => setMessages(r.data)).catch(() => {}), []);

  useEffect(() => {
    if (!localStorage.getItem("pv_token")) { navigate("/admin"); return; }
    api.get("/auth/me").catch(() => { localStorage.removeItem("pv_token"); navigate("/admin"); });
    loadAds(); loadMsgs();
  }, [navigate, loadAds, loadMsgs]);

  const logout = () => { localStorage.removeItem("pv_token"); navigate("/admin"); };

  const saveAd = async (form) => {
    try {
      if (editing === "new") await api.post("/ads", form);
      else await api.put(`/ads/${editing.id}`, form);
      toast.success(t("saved")); setEditing(null); loadAds();
    } catch { toast.error(t("inquiry_error")); }
  };

  // Immediately persist a newly prepared image to the ad being edited so the
  // old picture is replaced right away (no need to press Save for the image).
  const commitImage = async (form) => {
    if (!editing || editing === "new" || !editing.id) return;
    await api.put(`/ads/${editing.id}`, form);
    setEditing((prev) => (prev && prev !== "new" ? { ...prev, image: form.image } : prev));
    loadAds();
  };

  const deleteAd = async (ad) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/ads/${ad.id}`); toast.success(t("deleted")); loadAds();
  };

  const sendReply = async () => {
    await api.post(`/messages/${replyFor.id}/reply`, { reply: replyText });
    toast.success(t("reply_sent")); setReplyFor(null); setReplyText(""); loadMsgs();
  };

  const markRead = async (m) => { await api.put(`/messages/${m.id}/read`); loadMsgs(); };
  const deleteMsg = async (m) => { if (!window.confirm(t("confirm_delete"))) return; await api.delete(`/messages/${m.id}`); loadMsgs(); };

  const unreadCount = messages.filter((m) => !m.is_read).length;

  return (
    <div className="App min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[color:var(--c-glass-dark)] text-[color:var(--c-on-dark)] border-b border-black">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-home-link" className="flex items-center gap-3 hover:text-[color:var(--c-accent)] transition-colors">
              <Archive size={20} /><span className="font-serif text-2xl">{t("brand")}</span>
            </a>
            <span className="font-serif text-2xl text-[color:var(--c-on-dark)]/60">· {t("dashboard")}</span>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest hover:text-[color:var(--c-accent)] transition-colors">
            <LogOut size={15} /> {t("logout")}
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
        <Tabs defaultValue="ads">
          <TabsList className="rounded-none bg-[color:var(--c-surface)] p-1 h-auto">
            <TabsTrigger data-testid="tab-ads" value="ads" className="rounded-none data-[state=active]:bg-[color:var(--c-dark)] data-[state=active]:text-[color:var(--c-on-dark)] px-6 py-2.5 text-xs uppercase tracking-widest">{t("manage_ads")} ({ads.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-messages" value="messages" className="rounded-none data-[state=active]:bg-[color:var(--c-dark)] data-[state=active]:text-[color:var(--c-on-dark)] px-6 py-2.5 text-xs uppercase tracking-widest">
              {t("messages")} {unreadCount > 0 && <span className="ml-2 bg-[color:var(--c-accent)] text-white px-1.5 rounded-full text-[0.6rem]">{unreadCount}</span>}
            </TabsTrigger>
          </TabsList>

          {/* ADS */}
          <TabsContent value="ads" className="mt-8">
            <button data-testid="add-ad-button" onClick={() => setEditing("new")}
              className="inline-flex items-center gap-2 bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)] hover:bg-[color:var(--c-accent)] transition-colors px-5 py-3 text-xs uppercase tracking-widest font-semibold mb-6">
              <Plus size={15} /> {t("add_ad")}
            </button>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ads.map((ad) => (
                <div key={ad.id} data-testid={`admin-ad-${ad.id}`} className="border border-[color:var(--c-border)] bg-[color:var(--c-bg)]">
                  <div className="aspect-[16/10] bg-[color:var(--c-surface)] overflow-hidden">
                    <img src={mediaUrl(ad.image)} alt={ad.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <p className="pv-label">{ad.category}</p>
                    <h3 className="font-serif text-lg leading-tight mt-1 truncate">{ad.title}</h3>
                    <p className="text-sm text-[color:var(--c-ink2)] mt-1">{formatPrice(ad.price_usd)} · {ad.era}</p>
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`edit-ad-${ad.id}`} onClick={() => setEditing(ad)}
                        className="flex-1 inline-flex items-center justify-center gap-1 border border-[color:var(--c-ink)] py-2 text-xs uppercase tracking-wider hover:bg-[color:var(--c-dark)] hover:text-[color:var(--c-on-dark)] transition-colors">
                        <Pencil size={13} /> {t("edit")}
                      </button>
                      <button data-testid={`delete-ad-${ad.id}`} onClick={() => deleteAd(ad)}
                        className="inline-flex items-center justify-center gap-1 border border-[color:var(--c-accent)] text-[color:var(--c-accent)] px-3 py-2 text-xs uppercase tracking-wider hover:bg-[color:var(--c-accent)] hover:text-white transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages" className="mt-8">
            {messages.length === 0 ? (
              <p data-testid="no-messages" className="py-20 text-center font-serif text-2xl text-[color:var(--c-ink2)]">{t("no_messages")}</p>
            ) : (
              <div className="space-y-4">
                {messages.map((m) => (
                  <div key={m.id} data-testid={`message-${m.id}`}
                    className={`border bg-[color:var(--c-bg)] p-5 ${m.is_read ? "border-[color:var(--c-border)]" : "border-[color:var(--c-accent)]"}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2">
                          {m.is_read ? <MailOpen size={15} className="text-[color:var(--c-ink2)]" /> : <Mail size={15} className="text-[color:var(--c-accent)]" />}
                          <span className="font-semibold">{m.name}</span>
                          <span className="text-sm text-[color:var(--c-ink2)]">· {m.email}</span>
                          {!m.is_read && <Badge className="rounded-none bg-[color:var(--c-accent)] text-white text-[0.6rem] uppercase">{t("unread")}</Badge>}
                        </div>
                        <p className="pv-label mt-2">{t("regarding")}: {m.ad_title}</p>
                      </div>
                      <div className="flex gap-2">
                        {!m.is_read && (
                          <button data-testid={`markread-${m.id}`} onClick={() => markRead(m)}
                            className="border border-[color:var(--c-ink)] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[color:var(--c-dark)] hover:text-[color:var(--c-on-dark)] transition-colors">{t("mark_read")}</button>
                        )}
                        <button data-testid={`reply-${m.id}`} onClick={() => { setReplyFor(m); setReplyText(m.reply || ""); }}
                          className="inline-flex items-center gap-1 bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[color:var(--c-accent)] transition-colors">
                          <Reply size={13} /> {t("reply")}
                        </button>
                        <button data-testid={`deletemsg-${m.id}`} onClick={() => deleteMsg(m)}
                          className="border border-[color:var(--c-accent)] text-[color:var(--c-accent)] px-3 py-1.5 text-xs hover:bg-[color:var(--c-accent)] hover:text-white transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <p className="mt-3 text-[color:var(--c-ink)] leading-relaxed border-t border-[color:var(--c-border)] pt-3">{m.message}</p>
                    {m.reply && (
                      <div className="mt-3 bg-[color:var(--c-surface)] p-3 border-l-2 border-[color:var(--c-accent)]">
                        <p className="pv-label">{t("reply")}</p>
                        <p className="mt-1 text-sm text-[color:var(--c-ink)]">{m.reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Ad edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none bg-[color:var(--c-bg)] border-[color:var(--c-ink)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-3xl">{editing === "new" ? t("add_ad") : t("edit")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Edit advertisement details and image.</DialogDescription>
          {editing !== null && (
            <AdForm initial={editing === "new" ? EMPTY : editing} onSave={saveAd} onCancel={() => setEditing(null)} onCommit={editing === "new" ? undefined : commitImage} t={t} />
          )}
        </DialogContent>
      </Dialog>

      {/* Reply dialog */}
      <Dialog open={replyFor !== null} onOpenChange={(o) => !o && setReplyFor(null)}>
        <DialogContent className="rounded-none bg-[color:var(--c-bg)] border-[color:var(--c-ink)] max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-3xl">{t("reply")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Reply to the visitor inquiry.</DialogDescription>
          {replyFor && (
            <div className="space-y-4">
              <div className="bg-[color:var(--c-surface)] p-3 text-sm">
                <p className="pv-label">{t("from")}: {replyFor.name} ({replyFor.email})</p>
                <p className="mt-2">{replyFor.message}</p>
              </div>
              <Textarea data-testid="reply-textarea" rows={5} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                className="rounded-none bg-[color:var(--c-surface)] border-[color:var(--c-border)]" placeholder={t("your_message")} />
              <button data-testid="send-reply-button" onClick={sendReply}
                className="w-full bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)] hover:bg-[color:var(--c-accent)] transition-colors py-3 text-xs uppercase tracking-widest font-semibold">{t("send_reply")}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
